import React, { forwardRef, useState, useImperativeHandle, useRef } from "react";
import { Typography, Box, Divider, Button } from "@mui/material";

import Grid from "@mui/material/Grid";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import IconButton from "@mui/material/IconButton";
import { Input } from "../../components/Input";
import { DocumentUpload } from "../DocumentUpload";
import EditIcon from "@mui/icons-material/Edit";

const IncomeSource = forwardRef((props, ref) => {
    const [hasError, setHasError] = useState(false);
    const incomeTypeRef = useRef();
    const employerRef = useRef();
    const occupationRef = useRef();
    const employmentStartDateRef = useRef();
    const annualIncomeRef = useRef();
    const nameRef = useRef();
    const amountRef = useRef();
    const frequencyRef = useRef();

    const frequencyOptions = [
        {
            id: "WEEKLY",
            value: "Weekly",
        },
        {
            id: "BIWEEKLY",
            value: "Biweekly",
        },
        {
            id: "SEMI_MONTHLY",
            value: "Semi-monthly",
        },
        {
            id: "MONTHLY",
            value: "Monthly",
        },
    ];

    const incomeTypeOptions = [
        {
            id: "Traditional Employment",
            value: "Traditional Employment",
            ref: [employerRef, annualIncomeRef, employmentStartDateRef],
        },
        {
            id: "Self Employed",
            value: "Self Employed",
            ref: [employerRef, annualIncomeRef, employmentStartDateRef],
        },
        {
            id: "Independent Contractor",
            value: "Independent Contractor",
            ref: [nameRef, amountRef, frequencyRef],
        },
        {
            id: "Social Security / VA Disability",
            value: "Social Security / VA Disability",
            ref: [amountRef, frequencyRef],
        },
        {
            id: "Child Support Alimony",
            value: "Child Support Alimony",
            ref: [amountRef, frequencyRef],
        },
        {
            id: "Military Benefits",
            value: "Military Benefits",
            ref: [nameRef, amountRef, frequencyRef],
        },
        {
            id: "Annuity / Retirement / Pension",
            value: "Annuity / Retirement / Pension",
            ref: [nameRef, amountRef, frequencyRef],
        },
        {
            id: "Other Income Source",
            value: "Other Income Source",
            ref: [nameRef, amountRef, frequencyRef],
        },
        {
            id: "Payroll",
            value: "Payroll",
            ref: [],
        },
    ];

    const occupationOptions = [
        {
            id: "self",
            value: "Self",
        },
        {
            id: "business",
            value: "Business",
        },
        {
            id: "iTProfessional",
            value: "IT Professional",
        },
        {
            id: "none",
            value: "None",
        },
    ];

    const [formValues, setFormValues] = useState(
        props.value
            ? props.value
            : {
                  incomeType: "",
                  employer: "",
                  occupation: "",
                  employmentStartDate: "",
                  annualIncome: "",
              }
    );

    useImperativeHandle(ref, () => ({
        validate,
    }));

    const getRefs = () => {
        const type = incomeTypeOptions.find((item) => item.id === props.incomeType);
        return type?.ref;
    };

    const validate = () => {
        let valid = false;
        let refs = getRefs();
        if (refs?.length > 0) {
            for (let i = 0; i < refs.length; i++) {
                if (!refs[i]?.current?.validate()) {
                    valid = true;
                }
            }
        }

        setHasError(!valid);
        return !valid;
    };

    const handleChange = (id, value) => {
        setHasError(false);
        let newFormValues = formValues;
        newFormValues[id] = value;
        setFormValues(newFormValues);
        props.onChange(newFormValues);
    };

    // handle click event of the Remove button
    const handleRemoveClick = (index) => {
        props.onClick(props.index);
    };

    const handleEditClick = () => {
        props.onEdit(props.value);
    };

    const getFields = () => {
        if (props.incomeType === "Traditional Employment") {
            return (
                <Box>
                    <>
                        <Input
                            {...props.rest}
                            type="text"
                            ref={employerRef}
                            value={formValues.employer}
                            id="employer"
                            label="Employer"
                            name="employer"
                            margin="normal"
                            isRequired={true}
                            fullWidth
                            onChange={(value) => handleChange("employer", value)}
                        />

                        <Input
                            {...props.rest}
                            type="date"
                            value={formValues.employmentStartDate}
                            ref={employmentStartDateRef}
                            id="employmentStartDate"
                            label="Employment Start Date"
                            name="employmentStartDate"
                            margin="normal"
                            isRequired={true}
                            fullWidth
                            onChange={(value) => handleChange("employmentStartDate", value)}
                            mask="99 / 99 / 9999"
                            placeholder="MM / DD / YYYY"
                        />

                        <Input
                            {...props.rest}
                            ref={annualIncomeRef}
                            value={formValues.annualIncome}
                            type="number"
                            id="annualIncome"
                            label="Annual income"
                            name="annualIncome"
                            margin="normal"
                            fullWidth
                            onChange={(value) => handleChange("annualIncome", value)}
                            prefix={"$"}
                        />
                    </>
                    <Button
                        onClick={props.onNextIncomeClick}
                        color="primary"
                        fullWidth
                        variant="contained"
                        sx={{ height: "45px", mt: "1rem" }}
                        id="next-btn">
                        Next
                    </Button>
                </Box>
            );
        }
        return (
            <Box>
                {props.incomeType === "Self Employed" ? (
                    <>
                        <Input
                            {...props.rest}
                            type="text"
                            ref={employerRef}
                            value={formValues.employer}
                            id="employer"
                            label="Company Name"
                            name="employer"
                            margin="normal"
                            isRequired={true}
                            fullWidth
                            onChange={(value) => handleChange("employer", value)}
                        />
                        <Input
                            {...props.rest}
                            ref={annualIncomeRef}
                            value={formValues.annualIncome}
                            type="number"
                            id="annualIncome"
                            label="Annual Income"
                            name="annualIncome"
                            margin="normal"
                            isRequired={true}
                            fullWidth
                            onChange={(value) => handleChange("annualIncome", value)}
                            prefix={"$"}
                        />
                        <Input
                            {...props.rest}
                            type="date"
                            value={formValues.employmentStartDate}
                            ref={employmentStartDateRef}
                            id="employmentStartDate"
                            label="Self Employed Start Date"
                            name="employmentStartDate"
                            margin="normal"
                            isRequired={true}
                            fullWidth
                            onChange={(value) => handleChange("employmentStartDate", value)}
                            mask="99 / 99 / 9999"
                            placeholder="MM / DD / YYYY"
                        />
                    </>
                ) : (
                    <>
                        {!(
                            props.incomeType === "Social Security / VA Disability" ||
                            props.incomeType === "Child Support Alimony"
                        ) && (
                            <Input
                                {...props.rest}
                                type="text"
                                ref={nameRef}
                                value={formValues.name}
                                id="name"
                                label="Description"
                                name="name"
                                margin="normal"
                                isRequired={true}
                                fullWidth
                                onChange={(value) => handleChange("name", value)}
                            />
                        )}

                        <Input
                            {...props.rest}
                            ref={amountRef}
                            value={formValues.amount}
                            type="number"
                            id="amount"
                            label="Amount"
                            name="amount"
                            margin="normal"
                            isRequired={true}
                            fullWidth
                            onChange={(value) => handleChange("amount", value)}
                            prefix={"$"}
                        />
                        <Input
                            type="select"
                            ref={frequencyRef}
                            id={"frequency"}
                            label={"Frequency"}
                            value={formValues.frequency}
                            fullWidth
                            margin="normal"
                            options={frequencyOptions}
                            required
                            isRequired={true}
                            onChange={(value) => handleChange("frequency", value)}
                        />
                    </>
                )}

                <Button
                    onClick={props.onNextIncomeClick}
                    color="primary"
                    fullWidth
                    variant="contained"
                    sx={{ height: "45px", mt: "1rem" }}
                    id="next-btn">
                    Next
                </Button>
            </Box>
        );
    };

    return (
        <>
            {!props.singleIncome && (
                <Box
                    sx={{
                        p: 1,
                        borderRadius: "8px",
                        border: "1px solid #E6E8F0",
                        backgroundColor: "#F8F8F8",
                        overflow: "visible",
                        width: "100%",
                        mb: 2,
                    }}>
                    <Grid container spacing={2} justifyContent={"space-between"} alignItems={"center"}>
                        <Grid item>
                            <Typography gutterBottom variant="h5">
                                <b>{props.label}</b>
                            </Typography>
                        </Grid>
                        <Grid item>
                            <IconButton aria-label="delete" size="small" style={{ float: "right" }}>
                                <DeleteOutlinedIcon
                                    sx={{ color: "red" }}
                                    fontSize="small"
                                    onClick={() => handleRemoveClick(props.index)}
                                />
                            </IconButton>
                            {props.isEditable && !props.disabled && (
                                <IconButton aria-label="edit" size="small" style={{ float: "right" }}>
                                    <EditIcon
                                        sx={{ color: "gray" }}
                                        fontSize="small"
                                        onClick={() => handleEditClick()}
                                    />
                                </IconButton>
                            )}
                        </Grid>
                    </Grid>

                    <Divider sx={{ mt: 1, mb: 1 }} />
                    <Input
                        type="select"
                        ref={incomeTypeRef}
                        id={"incomeType"}
                        label={"Income type"}
                        value={formValues.incomeType}
                        fullWidth
                        options={incomeTypeOptions}
                        required
                        disabled={true}
                        readonly={true}
                        isRequired={true}
                        onChange={(value) => handleChange("incomeType", value)}
                    />
                    {props.incomeType === "Traditional Employment" ? (
                        <>
                            {/* {props.incomeType !== "Independent Contractor" && ( */}
                            <Input
                                {...props.rest}
                                type="text"
                                ref={employerRef}
                                value={formValues.employer}
                                id="employer"
                                label="Employer"
                                name="employer"
                                margin="normal"
                                readonly={true}
                                disabled={props.disabled}
                                fullWidth
                                onChange={(value) => handleChange("employer", value)}
                            />
                            {/* )
                            } */}
                            {/* {props.incomeType === "Independent Contractor" && (
                                        <Input
                                        {...props.rest}
                                        type="text"
                                        ref={nameRef}
                                        value={formValues.name}
                                        id="name"
                                        label="Description"
                                        name="name"
                                        margin="normal"
                                        isRequired={true}
                                        fullWidth
                                        maxLength={80}
                                        onChange={(value) => handleChange("name", value)}
                                        readonly={true}
                                    />)
                            } */}
                            <Input
                                {...props.rest}
                                type="date"
                                value={formValues.employmentStartDate}
                                ref={employmentStartDateRef}
                                id="employmentStartDate"
                                label="Employment Start Date"
                                name="employmentStartDate"
                                margin="normal"
                                fullWidth
                                onChange={(value) => handleChange("employmentStartDate", value)}
                                mask="99 / 99 / 9999"
                                disabled={props.disabled}
                                placeholder="MM / DD / YYYY"
                                readonly={true}
                            />
                            <Input
                                {...props.rest}
                                ref={annualIncomeRef}
                                value={formValues.annualIncome}
                                type="number"
                                id="annualIncome"
                                label="Annual income"
                                name="annualIncome"
                                margin="normal"
                                fullWidth
                                disabled={props.disabled}
                                onChange={(value) => handleChange("annualIncome", value)}
                                prefix={"$"}
                                readonly={true}
                            />
                        </>
                    ) : (
                        <>
                            {props.incomeType === "Self Employed" ? (
                                <>
                                    <Input
                                        {...props.rest}
                                        type="text"
                                        ref={employerRef}
                                        value={formValues.employer}
                                        id="employer"
                                        label="Company Name"
                                        name="employer"
                                        margin="normal"
                                        isRequired={true}
                                        fullWidth
                                        onChange={(value) => handleChange("employer", value)}
                                        readonly={true}
                                    />
                                    <Input
                                        {...props.rest}
                                        ref={annualIncomeRef}
                                        value={formValues.annualIncome}
                                        type="number"
                                        id="annualIncome"
                                        label="Annual Income"
                                        name="annualIncome"
                                        margin="normal"
                                        isRequired={true}
                                        fullWidth
                                        onChange={(value) => handleChange("annualIncome", value)}
                                        prefix={"$"}
                                        readonly={true}
                                    />
                                    <Input
                                        {...props.rest}
                                        type="date"
                                        value={formValues.employmentStartDate}
                                        ref={employmentStartDateRef}
                                        id="employmentStartDate"
                                        label="Self Employed Start Date"
                                        name="employmentStartDate"
                                        margin="normal"
                                        isRequired={true}
                                        fullWidth
                                        onChange={(value) => handleChange("employmentStartDate", value)}
                                        mask="99 / 99 / 9999"
                                        placeholder="MM / DD / YYYY"
                                        readonly={true}
                                    />
                                </>
                            ) : (
                                <>
                                    {!(
                                        props.incomeType === "Social Security / VA Disability" ||
                                        props.incomeType === "Child Support Alimony"
                                    ) && (
                                        <Input
                                            {...props.rest}
                                            type="text"
                                            ref={nameRef}
                                            value={formValues.name}
                                            id="name"
                                            label="Description"
                                            name="name"
                                            margin="normal"
                                            isRequired={true}
                                            fullWidth
                                            maxLength={80}
                                            onChange={(value) => handleChange("name", value)}
                                            readonly={true}
                                        />
                                    )}

                                    <Input
                                        {...props.rest}
                                        ref={amountRef}
                                        value={formValues.amount}
                                        type="number"
                                        id="amount"
                                        label="Amount"
                                        name="amount"
                                        margin="normal"
                                        isRequired={true}
                                        fullWidth
                                        onChange={(value) => handleChange("amount", value)}
                                        prefix={"$"}
                                        readonly={true}
                                    />
                                    <Input
                                        type="select"
                                        ref={frequencyRef}
                                        id={"frequency"}
                                        label={"Frequency"}
                                        value={formValues.frequency}
                                        fullWidth
                                        margin="normal"
                                        options={frequencyOptions}
                                        required
                                        isRequired={true}
                                        onChange={(value) => handleChange("frequency", value)}
                                        readonly={true}
                                    />
                                </>
                            )}
                        </>
                    )}

                    <Divider sx={{ my: "7px" }}></Divider>
                    <DocumentUpload parentId={formValues.id} readonly></DocumentUpload>
                </Box>
            )}
            {props.singleIncome && <>{getFields()}</>}
        </>
    );
});
export { IncomeSource };
